import * as React from "react";
import type { DataTableColumn } from "../../composites/data-table";
import { Timestamp } from "../../data-display/timestamp";

const NOW = Date.now();
const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

export type ListPageRecordStatus = "draft" | "processing" | "review" | "complete";

export interface ListPageRecord {
  id: string;
  title: string;
  status: ListPageRecordStatus;
  activeWorkflowRuns: number;
  completedWorkflowRuns: number;
  owner: string;
  createdAt: number;
  updatedAt: number;
}

const titles: string[] = [
  "Claim #C-2891 \u2014 water damage on 1428 Maple Ave",
  "Claim #C-2890 \u2014 hail damage on 87 Oakridge Dr",
  "Submission #S-1142 \u2014 Northwind Manufacturing renewal",
  "Submission #S-1141 \u2014 Elm Ridge Contractors GL",
  "Title order #T-7741 \u2014 312 Linden Ct refinance",
  "Title order #T-7740 \u2014 9 Birch Hollow purchase",
  "Mortgage file #M-4493 \u2014 Sterling Park condo",
  "Mortgage file #M-4492 \u2014 Harbor Crest townhomes",
  "Claim #C-2889 \u2014 fleet collision on I-95 milepost 22",
  "Submission #S-1140 \u2014 ABC Logistics fleet renewal",
  "Title order #T-7739 \u2014 1801 Aspen Ridge cash sale",
  "Mortgage file #M-4491 \u2014 Cypress Cove duplex",
  "Claim #C-2888 \u2014 burst pipe at Riverside warehouse",
  "Submission #S-1139 \u2014 Cascade Roofing workers comp",
  "Title order #T-7738 \u2014 47 Sunset Terrace sale",
  "Mortgage file #M-4490 \u2014 Maplewood single-family",
  "Claim #C-2887 \u2014 wind damage on 220 Cedar Ln",
  "Submission #S-1138 \u2014 Greenfield Transit auto",
  "Title order #T-7737 \u2014 88 Willow Bend refinance",
  "Mortgage file #M-4489 \u2014 Lakeshore loft conversion",
  "Claim #C-2886 \u2014 theft loss at 14 Industrial Pkwy",
  "Submission #S-1137 \u2014 Riverbend Construction CGL",
  "Title order #T-7736 \u2014 5 Highland Ridge cash purchase",
  "Mortgage file #M-4488 \u2014 Brookside garden home",
  "Claim #C-2885 \u2014 storm damage on 73 Beacon St",
];

const statuses: ListPageRecordStatus[] = ["draft", "processing", "review", "complete"];
const owners: string[] = ["Maya Chen", "Riley Morgan", "Devon Lee", "Priya Kapoor", "Alex Rivera"];

export const listPageRecordsFixture: ListPageRecord[] = titles.map((title, i) => {
  const status = statuses[i % statuses.length] ?? "draft";
  const active = status === "processing" || status === "review" ? (i % 3) + 1 : 0;
  const completed = status === "complete" ? 4 : status === "review" ? 2 : i % 2;
  return {
    id: `rec_${(1000 + i).toString(36)}`,
    title,
    status,
    activeWorkflowRuns: active,
    completedWorkflowRuns: completed,
    owner: owners[i % owners.length] ?? "Maya Chen",
    createdAt: NOW - (i + 1) * 4 * HOUR,
    updatedAt: NOW - (i + 1) * HOUR - (i % 7) * 17 * 60 * 1000,
  };
});

function statusBadgeClass(status: ListPageRecordStatus): string {
  switch (status) {
    case "complete":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300";
    case "review":
      return "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300";
    case "processing":
      return "border-foreground/15 bg-foreground/[0.04] text-foreground/80";
    case "draft":
    default:
      return "border-border bg-muted/40 text-muted-foreground";
  }
}

export const listPageRecordColumns: DataTableColumn<ListPageRecord>[] = [
  {
    key: "title",
    label: "Title",
    sortable: true,
    mobilePrimary: true,
    render: (_value, row) => <span className="font-medium text-foreground">{row.title}</span>,
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
    width: "120px",
    render: (_value, row) => (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${statusBadgeClass(
          row.status,
        )}`}
      >
        <span className="size-1 rounded-full bg-current opacity-70" />
        {row.status}
      </span>
    ),
  },
  {
    key: "activeWorkflowRuns",
    label: "Workflows",
    mobileHidden: true,
    width: "160px",
    render: (_value, row) => {
      const total = row.activeWorkflowRuns + row.completedWorkflowRuns;
      if (total === 0) {
        return <span className="text-xs text-muted-foreground/60">{"\u2014"}</span>;
      }
      return (
        <div className="flex items-center gap-3 text-[12px] tabular-nums">
          {row.activeWorkflowRuns > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-foreground">
              <span className="size-1.5 rounded-full bg-foreground/70" />
              {row.activeWorkflowRuns} active
            </span>
          ) : null}
          {row.completedWorkflowRuns > 0 ? (
            <span className="text-muted-foreground">{row.completedWorkflowRuns} done</span>
          ) : null}
        </div>
      );
    },
  },
  {
    key: "owner",
    label: "Owner",
    mobileHidden: true,
    width: "160px",
    render: (_value, row) => <span className="text-sm text-muted-foreground">{row.owner}</span>,
  },
  {
    key: "createdAt",
    label: "Created",
    sortable: true,
    mobileHidden: true,
    width: "140px",
    render: (_value, row) => (
      <Timestamp date={row.createdAt} className="text-sm text-muted-foreground" />
    ),
  },
];
